import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentModelFactoryService, DocumentModelType, Phase, PhaseType, PlanDocumentUtils as Utils } from 'fitforce-document-sync';
import { cloneDeep, debounce, remove } from 'lodash';
import * as moment from 'moment';
import { Day } from '../document-calendar.component';

export interface AddEditPhaseDialogData {
  phaseTypes: PhaseType[];
  startDay?: Day;
  endDay?: Day;
  phase?: Phase;
  planStartDate: moment.Moment;
  planEndDate: moment.Moment;
  phases: Phase[];
}

@Component({
  selector: 'add-edit-phase-dialog',
  templateUrl: './add-edit-phase-dialog.component.html',
  styleUrls: ['./add-edit-phase-dialog.component.scss'],
})
export class AddEditPhaseDialogComponent implements OnInit {
  private debouncedNameChanged = debounce(this.nameChanged.bind(this), 500);

  title: string;
  saveButtonText: string;

  formGroup: FormGroup;
  isStandardPhase: boolean;
  filteredPhaseTypes: PhaseType[] = [];

  readonly colors: { name: string; value: string }[] = [
    { value: 'blue', name: 'Blue' },
    { value: 'brown', name: 'Brown' },
    { value: 'green', name: 'Green' },
    { value: 'orange', name: 'Orange' },
    { value: 'purple', name: 'Purple' },
    { value: 'red', name: 'Red' },
    { value: 'yellow', name: 'Yellow' },
    { value: 'lightyellow', name: 'Light Yellow' },
    { value: 'lightred', name: 'Light Red' },
    { value: 'lightgreen', name: 'Light Green' },
    { value: 'lightblue', name: 'Light Blue' },
    { value: 'lightpurple', name: 'Light Purple' },
  ];

  hasErrors: boolean;
  hasConflicts: boolean;
  errors: { type: string; message: string }[] = [];
  conflictOperations: Function[] = [];

  constructor(
    public dialog: MatDialogRef<AddEditPhaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddEditPhaseDialogData,
    private formBuilder: FormBuilder,
    private factory: DocumentModelFactoryService,
  ) {
    this.title = data.phase ? 'Edit Phase' : 'Create Phase';
    this.saveButtonText = data.phase ? 'Save' : 'Create';
  }

  /**
   * Initialzes form controls
   */
  ngOnInit() {
    if (this.data.phase) {
      const foundPhaseType = this.data.phaseTypes.find(type => type.id === this.data.phase.exercise_program_phase_id);
      this.isStandardPhase = !!foundPhaseType;

      this.formGroup = this.formBuilder.group({
        startDate: this.formBuilder.control(
          Utils.getDate(this.data.planStartDate, this.data.phase.start_day, this.data.phase.start_week).toDate(),
        ),
        endDate: this.formBuilder.control(
          Utils.getDate(this.data.planStartDate, this.data.phase.end_day, this.data.phase.end_week).toDate(),
        ),
        name: this.formBuilder.control(foundPhaseType || this.data.phase.name),
        color: this.formBuilder.control(this.data.phase ? (foundPhaseType ? foundPhaseType.color : this.data.phase.color) : null),
      });
    } else if (this.data.startDay && this.data.endDay) {
      this.formGroup = this.formBuilder.group({
        startDate: this.formBuilder.control(this.data.startDay.date.toDate()),
        endDate: this.formBuilder.control(this.data.endDay.date.toDate()),
        name: this.formBuilder.control(null),
        color: this.formBuilder.control(null),
      });
    } else {
      this.formGroup = this.formBuilder.group({
        startDate: this.formBuilder.control(null),
        endDate: this.formBuilder.control(null),
        name: this.formBuilder.control(null),
        color: this.formBuilder.control(null),
      });
    }

    this.filteredPhaseTypes = this.data.phaseTypes;

    this.checkForConflicts();
  }

  /**
   * Helper to get form controls from group
   */
  get controls() {
    return {
      startDate: this.formGroup.get('startDate'),
      endDate: this.formGroup.get('endDate'),
      name: this.formGroup.get('name'),
      color: this.formGroup.get('color'),
    };
  }

  /**
   * Called when dates have changed.
   */
  dateChanged() {
    this.checkForConflicts();
  }

  /**
   * Called on name input change and debounces
   * @param $event
   */
  debounceNameChanged($event) {
    this.debouncedNameChanged($event);
  }

  /**
   * Detects if entered phase is standard or custom and sets the color control value if standard.
   */
  nameChanged($event: any | MatAutocompleteSelectedEvent) {
    let foundPhaseType: PhaseType;
    if ($event instanceof MatAutocompleteSelectedEvent) {
      const option: PhaseType = $event.option.value;
      foundPhaseType = this.data.phaseTypes.find(type => type.id === option.id);
      this.controls.color.setValue(option.color);
    } else if ($event instanceof Event) {
      foundPhaseType = this.data.phaseTypes.find(type => type.name.toLowerCase() === (<any>$event.target).value.toLowerCase());
      this.filteredPhaseTypes = this.data.phaseTypes.filter(phase =>
        phase.name.toLowerCase().indexOf((<any>$event.target).value.toLowerCase()) > -1,
      );
    } else {
      foundPhaseType = this.data.phaseTypes.find(type => type.name.toLowerCase() === this.controls.name.value.toLowerCase());
      this.filteredPhaseTypes = this.data.phaseTypes.filter(phase =>
        phase.name.toLowerCase().indexOf(this.controls.name.value.toLowerCase()) > -1,
      );
    }

    this.isStandardPhase = !!foundPhaseType;
  }

  /**
   * Formats autocomplete input text when item is selected
   * @param value
   */
  displayFn(value: any): string {
    return value ? (value.hasOwnProperty('name') ? value.name : value) : '';
  }

  /**
   * Called on submit. Creates or replaces a phase and sends results to parent.
   */
  submit($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.conflictOperations.forEach(operation => {
      operation.bind(this)();
    });

    const startSchedule = Utils.getSchedule(this.data.planStartDate, moment(this.controls.startDate.value));
    const endSchedule = Utils.getSchedule(this.data.planStartDate, moment(this.controls.endDate.value));

    const phase = this.factory.build<Phase>(DocumentModelType.Phase);
    phase.end_day = endSchedule.day;
    phase.end_week = endSchedule.week;
    phase.start_day = startSchedule.day;
    phase.start_week = startSchedule.week;

    if (this.isStandardPhase) {
      phase.exercise_program_phase_id = this.controls.name.value.id;
      phase.name = this.controls.name.value.name;
    } else {
      phase.color = this.controls.color.value;
      phase.name = this.controls.name.value;
    }

    const phases = cloneDeep<Phase[]>(this.data.phases.filter(p => !this.data.phase || (this.data.phase && this.data.phase.id !== p.id)));
    phases.push(phase);

    this.dialog.close(phases);
  }

  /**
   * Called on delete. Removes the phase and sends result to parent.
   */
  delete() {
    const phases = cloneDeep<Phase[]>(this.data.phases);
    remove(phases, phase => phase.id === this.data.phase.id);

    this.dialog.close(phases);
  }

  /**
   * Checks for any conflicts with other phases in the plan, displays errors or warnings,
   * and queues any fixes to conflicts that are called before save.
   */
  checkForConflicts() {
    this.hasErrors = false;
    this.hasConflicts = false;
    this.errors = [];
    this.conflictOperations = [];

    const startDate: moment.Moment = moment(this.controls.startDate.value);
    const endDate: moment.Moment = moment(this.controls.endDate.value);

    // confirm dates are valid
    if (!this.controls.startDate.valid && startDate.isValid()) {
      this.errors.push({
        type: 'error',
        message: 'Invalid start date',
      });
      this.hasErrors = true;
    } else if (!this.controls.endDate.valid && endDate.isValid()) {
      this.errors.push({
        type: 'error',
        message: 'Invalid end date',
      });
      this.hasErrors = true;
    } else if (endDate.isBefore(startDate)) {
      this.errors.push({
        type: 'error',
        message: 'End date is before start date',
      });
      this.hasErrors = true;
    } else if (startDate.isSame(endDate)) {
      this.errors.push({
        type: 'error',
        message: 'Start and end date are the same.',
      });
      this.hasErrors = true;
    } else {
      this.data.phases
        .filter(phase => !this.data.phase || (this.data.phase && this.data.phase.id !== phase.id))
        .forEach(phase => {
          const phaseStartDate = Utils.getDate(this.data.planStartDate, phase.start_day, phase.start_week);
          const phaseEndDate = Utils.getDate(this.data.planStartDate, phase.end_day, phase.end_week);

          // if the start and end dates match another phase exactly
          // or if another phase is completely consumed
          if (isSameOrBetween(phaseStartDate, startDate, endDate) && isSameOrBetween(phaseEndDate, startDate, endDate)) {
            this.hasConflicts = true;

            // push a resolution to delete the consumed phase
            this.conflictOperations.push(() => remove(this.data.phases, p => p.id === phase.id));

            return this.errors.push({
              type: 'warning',
              message: phase.name + ' will be deleted',
            });
          }

          // if the dates conflict
          const hasStartConflict = isSameOrBetween(startDate, phaseStartDate, phaseEndDate);
          const hasEndConflict = isSameOrBetween(endDate, phaseStartDate, phaseEndDate);

          if (hasStartConflict && hasEndConflict) {
            this.errors.push({
              type: 'warning',
              message: phase.name + ' will be deleted',
            });

            // push a resolution to delete the conflicting phase
            this.conflictOperations.push(() => remove(this.data.phases, p => p.id === phase.id));
          } else if (hasStartConflict) {
            this.errors.push({
              type: 'warning',
              message: phase.name + ' will have its end date moved back',
            });

            // push a resolution to update the `endDate` property of the conflicting
            // phase to be 1 day before the start of the new phase
            this.conflictOperations.push(() => {
              const date = startDate.clone().subtract(1, 'd');
              const schedule = Utils.getSchedule(this.data.planStartDate, date);
              phase.end_day = schedule.day;
              phase.end_week = schedule.week;
            });
          } else if (hasEndConflict) {
            this.errors.push({
              type: 'warning',
              message: phase.name + ' will have its start date moved forward',
            });

            // push a resolution to update the `startDate` property of the conflicting
            // phase to be 1 day after the end of the new phase
            this.conflictOperations.push(() => {
              const date = endDate.clone().add(1, 'd');
              const schedule = Utils.getSchedule(this.data.planStartDate, date);
              phase.start_day = schedule.day;
              phase.start_week = schedule.week;
            });
          }
        });

      this.hasConflicts = this.errors.length > 0;
    }
  }
}

function isSameOrBetween(date, start, end) {
  if (date.isSame(start)) {
    return true;
  }
  if (date.isSame(end)) {
    return true;
  }
  return date.isBetween(start, end);
}
